import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { suggestInterventionGroups as callSuggestInterventionGroups } from '../lib/functions';
import { collection, getDocs, doc, updateDoc, addDoc, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { useStudents } from '../lib/useStudents';
import { useSchoolId } from '../lib/auth/schoolContext';
import { groupSizeError } from '../lib/interventionLimits';

const InterventionSuggestions = () => {
  const [suggestions, setSuggestions] = useState([]);
  const { students } = useStudents();
  const { schoolId, loading: schoolIdLoading } = useSchoolId();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  // Default to 'available' — the previous probe-with-bad-args check polluted the
  // console with a 400. Real errors will surface from generateSuggestions itself.
  const [functionStatus, setFunctionStatus] = useState('available');
  const [activeTab, setActiveTab] = useState('interventions');
  const [reviewForm, setReviewForm] = useState({
    suggestionId: '',
    status: 'accepted',
    reviewNotes: ''
  });

  useEffect(() => {
    if (schoolIdLoading) return;
    if (schoolId) {
      fetchSuggestions();
    } else {
      setLoading(false);
    }
  }, [schoolId, schoolIdLoading]);

  const getStudentLabel = (item) => {
    // Accept id string or object with id/name
    const sid = typeof item === 'string' ? item : (item?.id || item?.studentId || '');
    const s = sid ? students.find(st => st.id === sid) : undefined;
    if (!s) {
      const fallback = typeof item === 'object' ? (item.name || item.fullName) : '';
      return fallback || 'Unknown';
    }
    const name = `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.name || 'Unknown';
    const year = s.yearGroup ? ` • ${s.yearGroup}` : '';
    const ehcp = s.hasEHCP ? ' • EHCP' : '';
    return `${name}${year}${ehcp}`;
  };

  const getStudentLabelForSuggestion = (suggestion, sid) => {
    // Try live student list first
    const s = students.find(st => st.id === sid);
    if (s) {
      const name = `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.name || 'Unknown';
      const year = s.yearGroup ? ` • ${s.yearGroup}` : '';
      const ehcp = s.hasEHCP ? ' • EHCP' : '';
      return `${name}${year}${ehcp}`;
    }
    // Fallback to studentSummaries saved on the suggestion (may reference old IDs)
    const sm = Array.isArray(suggestion?.studentSummaries) ? suggestion.studentSummaries.find(x => x.id === sid) : null;
    if (sm && sm.name) return sm.name;
    return 'Unknown';
  };

  // Pre-flight status probe removed — it called the function with bad args
  // purely to test reachability, generating an HTTP 400 in the console on every
  // page load. Real errors surface from the actual generate call below.

  const fetchSuggestions = async () => {
    if (!schoolId) return;
    try {
      setLoading(true);
      const q = query(
        collection(db, 'interventionSuggestions'),
        where('schoolId', '==', schoolId),
        orderBy('createdAt', 'desc')
      );
      const suggestionsSnapshot = await getDocs(q);
      const suggestionsData = suggestionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSuggestions(suggestionsData);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      console.error('Error details:', error.code, error.message);
      toast.error('Failed to fetch suggestions');
    } finally {
      setLoading(false);
    }
  };

  const generateSuggestions = async () => {
    try {
      setGenerating(true);
      const user = auth.currentUser;
      if (!user) throw new Error('No user logged in');
      if (!schoolId) throw new Error('No school context available');
      if (!students || students.length === 0) {
        toast.error('No students found. Add students before generating suggestions.');
        return;
      }

      // Build the payload the Cloud Function expects. The handler validates
      // every student has id/firstName/lastName/yearGroup/phase/specialNeeds.
      const inferPhase = (yearGroup) => {
        const yg = String(yearGroup || '').toLowerCase();
        // Years 7+ are secondary in the UK system; everything else (Reception,
        // Years 1–6) is primary.
        const yearNum = parseInt(yg.replace(/[^0-9]/g, ''), 10);
        if (!Number.isNaN(yearNum) && yearNum >= 7) return 'secondary';
        return 'primary';
      };

      const studentPayload = students.map((s) => {
        const secondary = Array.isArray(s.secondaryNeeds) ? s.secondaryNeeds.filter(Boolean) : [];
        const allNeeds = Array.isArray(s.specialNeeds) && s.specialNeeds.length
          ? s.specialNeeds
          : [s.primaryNeed, ...secondary].filter(Boolean);
        return {
          id: s.id,
          firstName: s.firstName || s.name?.split(' ')[0] || 'Unknown',
          lastName: s.lastName || s.name?.split(' ').slice(1).join(' ') || 'Unknown',
          yearGroup: s.yearGroup || 'Year 1',
          phase: s.phase || inferPhase(s.yearGroup),
          specialNeeds: allNeeds,
          secondaryNeeds: secondary,
          primaryNeed: s.primaryNeed || '',
          hasEHCP: !!s.hasEHCP,
          academicLevel: s.academicLevel || 'working at',
          workingAtLevel: s.workingAtLevel || s.academicLevel || '',
          attendance: typeof s.attendance === 'number' ? s.attendance : undefined,
        };
      });

      toast.loading(`Asking AI to suggest groups for ${studentPayload.length} students…`, { id: 'gen-suggest' });
      const { data: result } = await callSuggestInterventionGroups({ students: studentPayload });
      toast.dismiss('gen-suggest');

      const groups = Array.isArray(result?.groups) ? result.groups : [];
      if (groups.length === 0) {
        toast('AI returned no suggestions for the current cohort.', { icon: 'ℹ️' });
        return;
      }

      // Persist each group as an `interventionSuggestions` doc so the list
      // view (which filters by `targetAreas`) picks it up.
      const studentSummaries = students.map((s) => ({
        id: s.id,
        name: `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.name || 'Unknown',
      }));

      await Promise.all(groups.map((g) => addDoc(collection(db, 'interventionSuggestions'), {
        schoolId,
        name: g.name,
        description: g.description || '',
        type: g.interventionType || 'General',
        targetAreas: Array.isArray(g.targetedNeeds) ? g.targetedNeeds : [],
        rationale: Array.isArray(g.goals) ? g.goals.join('; ') : '',
        estimatedFrequency: g.suggestedSchedule?.frequency || '',
        estimatedDuration: g.suggestedSchedule?.duration || '',
        suggestedSchedule: g.suggestedSchedule || null,
        goals: Array.isArray(g.goals) ? g.goals : [],
        confidence: 8,
        status: 'pending',
        students: Array.isArray(g.studentIds) ? g.studentIds : [],
        studentSummaries,
        createdAt: serverTimestamp(),
        generatedBy: user.uid,
      })));

      toast.success(`Generated ${groups.length} intervention suggestion${groups.length === 1 ? '' : 's'}`);
      fetchSuggestions();
    } catch (error) {
      toast.dismiss('gen-suggest');
      console.error('Error generating suggestions:', error);
      const msg = error?.message || 'Unknown error';
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
        toast.error('Cannot reach the Cloud Function. Is the functions emulator running on :5001?');
      } else if (msg.includes('No user logged in')) {
        toast.error('Please log in to generate suggestions');
      } else if (msg.includes('OPENAI_API_KEY')) {
        toast.error('AI service is not configured. Ask your admin to set OPENAI_API_KEY on Cloud Functions, then redeploy.');
      } else {
        toast.error(`Failed to generate suggestions: ${msg}`);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleReview = async (suggestionId, status, reviewNotes) => {
    try {
      // Update the suggestion status
      await updateDoc(doc(db, 'interventionSuggestions', suggestionId), {
        status,
        reviewNotes: reviewNotes || '',
        reviewedBy: 'current-user', // TODO: Get from auth context
        reviewedAt: new Date()
      });

      // If accepted, create the actual intervention or grouping
      if (status === 'accepted') {
        const suggestion = suggestions.find(s => s.id === suggestionId);
        if (suggestion) {
          if (suggestion.targetAreas) {
            // It's an intervention suggestion
            await createInterventionFromSuggestion(suggestion);
          } else if (suggestion.sharedNeeds) {
            // It's a grouping suggestion
            await createGroupingFromSuggestion(suggestion);
          }
        }
      }

      toast.success(`Suggestion ${status}`);
      fetchSuggestions();
      setReviewForm({ suggestionId: '', status: 'accepted', reviewNotes: '' });
    } catch (error) {
      console.error('Error reviewing suggestion:', error);
      toast.error('Failed to review suggestion');
    }
  };

  const createInterventionFromSuggestion = async (suggestion) => {
    const cohort = suggestion.students || [];
    const sizeErr = groupSizeError(cohort.length);
    if (sizeErr) {
      toast.error(sizeErr);
      return;
    }
    try {
      const interventionData = {
        name: suggestion.name,
        type: suggestion.type || suggestion.sectionFAlignment?.inferredType || 'Other',
        description: suggestion.description,
        students: suggestion.students,
        targetAreas: suggestion.targetAreas,
        frequency: suggestion.estimatedFrequency || suggestion.sectionFAlignment?.inferredFrequency || 'Weekly',
        duration: suggestion.estimatedDuration || suggestion.sectionFAlignment?.inferredDuration || '30 minutes',
        notes: `Created from suggestion: ${suggestion.rationale}`,
        createdAt: new Date(),
        status: 'active',
        createdBy: 'system',
        sessions: []
      };

      await addDoc(collection(db, 'interventions'), { ...interventionData, schoolId });
      toast.success('Intervention created successfully');
    } catch (error) {
      console.error('Error creating intervention:', error);
      toast.error('Failed to create intervention');
    }
  };

  const createGroupingFromSuggestion = async (suggestion) => {
    const cohort = suggestion.students || [];
    const sizeErr = groupSizeError(cohort.length);
    if (sizeErr) {
      toast.error(sizeErr);
      return;
    }
    try {
      const groupingData = {
        name: suggestion.name,
        description: suggestion.description,
        students: suggestion.students,
        sharedNeeds: suggestion.sharedNeeds,
        communicationMethods: suggestion.communicationMethods,
        yearGroupRange: suggestion.yearGroupRange,
        createdAt: new Date(),
        status: 'active',
        createdBy: 'system'
      };

      await addDoc(collection(db, 'studentGroupings'), { ...groupingData, schoolId });
      toast.success('Student grouping created successfully');
    } catch (error) {
      console.error('Error creating grouping:', error);
      toast.error('Failed to create grouping');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 8) return 'bg-green-100 text-green-800';
    if (confidence >= 6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const interventionSuggestions = suggestions.filter(
    (s) => s.targetAreas && s.suggestionKind !== 'interventionGroup',
  );
  const groupingSuggestions = suggestions.filter(s => s.sharedNeeds);

  if (loading || schoolIdLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!schoolId) {
    return (
      <div className="max-w-7xl mx-auto text-center py-12 text-gray-500">
        <p>No school context available. Please ensure your account is linked to a school.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">AI Intervention Suggestions</h1>
            <p className="text-gray-600 mt-2">
              AI-powered suggestions for interventions and student groupings based on shared needs
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={generateSuggestions}
              disabled={generating}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {generating ? 'Generating...' : 'Generate New Suggestions'}
            </button>
            
                          {functionStatus === 'unavailable' && (
                <div className="text-sm text-red-500 bg-red-100 px-3 py-2 rounded-lg border border-red-200">
                  Cloud Function Not Available: Please deploy the function first
                </div>
              )}

              {functionStatus === 'available' && (
                <div className="text-sm text-green-500 bg-green-100 px-3 py-2 rounded-lg border border-green-200">
                  Cloud Function Available
                </div>
              )}

              {functionStatus === 'checking' && (
                <div className="text-sm text-yellow-500 bg-yellow-100 px-3 py-2 rounded-lg border border-yellow-200">
                  Checking Function Status...
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 rounded-lg shadow-sm mb-8">
        <div className="px-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('interventions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'interventions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Intervention Suggestions ({interventionSuggestions.length})
            </button>
            <button
              onClick={() => setActiveTab('groupings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'groupings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Grouping Suggestions ({groupingSuggestions.length})
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'interventions' && (
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Intervention Suggestions</h2>
            {interventionSuggestions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2">No intervention suggestions found. Click "Generate New Suggestions" to get started.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {interventionSuggestions.map((suggestion) => (
                  <div key={suggestion.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">{suggestion.name}</h3>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(suggestion.status)}`}>
                        {suggestion.status}
                      </span>
                    </div>
                    
                    <div className="space-y-3 mb-4">
                      <p className="text-gray-600">{suggestion.description}</p>
                      <p className="text-sm text-gray-500"><strong>Rationale:</strong> {suggestion.rationale}</p>
                      <p className="text-sm text-gray-500"><strong>Type:</strong> {suggestion.type}</p>
                      <p className="text-sm text-gray-500"><strong>Frequency:</strong> {suggestion.estimatedFrequency}</p>
                      <p className="text-sm text-gray-500"><strong>Duration:</strong> {suggestion.estimatedDuration}</p>
                      
                      <div className="flex items-center space-x-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getConfidenceColor(suggestion.confidence)}`}>
                          Confidence: {suggestion.confidence}/10
                        </span>
                      </div>
                      {suggestion.students && suggestion.students.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Students:</p>
                          <div className="flex flex-wrap gap-1">
                            {suggestion.students.map((sid) => (
                              <span key={sid} className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                                {getStudentLabelForSuggestion(suggestion, sid)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {suggestion.sectionFAlignment && (
                        <div className="mt-2">
                          <p className="text-sm font-medium text-gray-700 mb-1">Section F alignment</p>
                          <div className="text-xs text-gray-600">
                            {(suggestion.sectionFAlignment.inferredType || suggestion.sectionFAlignment.inferredFrequency || suggestion.sectionFAlignment.inferredDuration) && (
                              <div className="mb-1">
                                {suggestion.sectionFAlignment.inferredType ? `Type: ${suggestion.sectionFAlignment.inferredType}` : ''}
                                {suggestion.sectionFAlignment.inferredFrequency ? ` • Frequency: ${suggestion.sectionFAlignment.inferredFrequency}` : ''}
                                {suggestion.sectionFAlignment.inferredDuration ? ` • Duration: ${suggestion.sectionFAlignment.inferredDuration}` : ''}
                              </div>
                            )}
                            {Array.isArray(suggestion.sectionFAlignment.matchedProvisions) && suggestion.sectionFAlignment.matchedProvisions.length > 0 && (
                              <div>
                                <div className="text-gray-500">Matched provisions (sample):</div>
                                <ul className="list-disc pl-5">
                                  {suggestion.sectionFAlignment.matchedProvisions.map((p, i) => (
                                    <li key={i}>{p}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {suggestion.status === 'pending' && (
                      <div className="space-y-3">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleReview(suggestion.id, 'accepted')}
                            className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => handleReview(suggestion.id, 'rejected')}
                            className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    )}

                    {suggestion.status !== 'pending' && (
                      <div className="text-sm text-gray-500">
                        <p><strong>Reviewed by:</strong> {suggestion.reviewedBy || 'Unknown'}</p>
                        {suggestion.reviewNotes && (
                          <p><strong>Notes:</strong> {suggestion.reviewNotes}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'groupings' && (
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Grouping Suggestions</h2>
            {groupingSuggestions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="mt-2">No grouping suggestions found. Click "Generate New Suggestions" to get started.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {groupingSuggestions.map((suggestion) => (
                  <div key={suggestion.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">{suggestion.name}</h3>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(suggestion.status)}`}>
                        {suggestion.status}
                      </span>
                    </div>
                    
                    <div className="space-y-3 mb-4">
                      <p className="text-gray-600">{suggestion.description}</p>
                      <p className="text-sm text-gray-500"><strong>Rationale:</strong> {suggestion.rationale}</p>
                      <p className="text-sm text-gray-500"><strong>Year Groups:</strong> {suggestion.yearGroupRange}</p>
                      <p className="text-sm text-gray-500"><strong>Group Size:</strong> {suggestion.estimatedSize} students</p>
                      
                      <div className="flex items-center space-x-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getConfidenceColor(suggestion.confidence)}`}>
                          Confidence: {suggestion.confidence}/10
                        </span>
                      </div>

                      {suggestion.students && suggestion.students.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Students:</p>
                          <div className="flex flex-wrap gap-1">
                            {suggestion.students.map((sid) => (
                              <span key={sid} className="inline-flex px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded-full">
                                {getStudentLabelForSuggestion(suggestion, sid)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {suggestion.sectionFAlignment && (
                        <div className="mt-2">
                          <p className="text-sm font-medium text-gray-700 mb-1">Section F alignment</p>
                          <div className="text-xs text-gray-600">
                            {(suggestion.sectionFAlignment.inferredType || suggestion.sectionFAlignment.inferredFrequency || suggestion.sectionFAlignment.inferredDuration) && (
                              <div className="mb-1">
                                {suggestion.sectionFAlignment.inferredType ? `Type: ${suggestion.sectionFAlignment.inferredType}` : ''}
                                {suggestion.sectionFAlignment.inferredFrequency ? ` • Frequency: ${suggestion.sectionFAlignment.inferredFrequency}` : ''}
                                {suggestion.sectionFAlignment.inferredDuration ? ` • Duration: ${suggestion.sectionFAlignment.inferredDuration}` : ''}
                              </div>
                            )}
                            {Array.isArray(suggestion.sectionFAlignment.matchedProvisions) && suggestion.sectionFAlignment.matchedProvisions.length > 0 && (
                              <div>
                                <div className="text-gray-500">Matched provisions (sample):</div>
                                <ul className="list-disc pl-5">
                                  {suggestion.sectionFAlignment.matchedProvisions.map((p, i) => (
                                    <li key={i}>{p}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {suggestion.sharedNeeds && suggestion.sharedNeeds.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Shared Needs:</p>
                          <div className="flex flex-wrap gap-1">
                            {suggestion.sharedNeeds.map((need, index) => (
                              <span key={index} className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                {need}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {suggestion.communicationMethods && suggestion.communicationMethods.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Communication Methods:</p>
                          <div className="flex flex-wrap gap-1">
                            {suggestion.communicationMethods.map((method, index) => (
                              <span key={index} className="inline-flex px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                                {method}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {suggestion.status === 'pending' && (
                      <div className="space-y-3">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleReview(suggestion.id, 'accepted')}
                            className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => handleReview(suggestion.id, 'rejected')}
                            className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    )}

                    {suggestion.status !== 'pending' && (
                      <div className="text-sm text-gray-500">
                        <p><strong>Reviewed by:</strong> {suggestion.reviewedBy || 'Unknown'}</p>
                        {suggestion.reviewNotes && (
                          <p><strong>Notes:</strong> {suggestion.reviewNotes}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InterventionSuggestions; 
